/** The main-thread side of the AI Worker (D7).
 *
 * `WorkerAiHost` owns one Worker per game and keeps it a replay client: it
 * listens to every command the session accepts, batches them, and flushes the
 * batch ahead of each `plan`, so the Worker's state is always the main thread's
 * state at the version the plan names. `WorkerAiController` is the per-seat
 * `PlayerController` the loop drives; it asks the host and, when the host has
 * nothing (an abort that outran the Worker, a desync, a Worker that failed to
 * load), hands that one turn to the same main-thread controller the headless
 * runner uses. The loop never learns which one answered.
 *
 * The desync check is the M3.5 gate ("Worker desync check never fires"). On
 * the first plan of each computer player-turn the Worker returns its state
 * hash with the command and the host hashes its own state at the same
 * version; a mismatch is warned once with both hashes, counted on `desyncs`,
 * and answered with a full re-init - the one structured clone of the whole
 * state D7 allows after game start.
 */

import type { ContentRegistry, GameCommand, GameState, PlayerId, PlayerObservation } from "@game/core";
import { hashState } from "@game/core";
import type { AiController, PlayerController } from "@game/ai";
import type { WorkerReply, WorkerRequest, WorkerSetup } from "./aiWorkerBody.js";

/** What the host needs from the session, structurally satisfied by `GameSession`. */
export interface CommandFeed {
  getState(): GameState;
  getContent(): ContentRegistry;
  /** Called with every command the kernel accepted and the version it produced. */
  onCommand(listener: (command: GameCommand, version: number) => void): () => void;
}

/** A Worker failing outside the protocol - a script that did not load, an
 *  uncaught throw. Raised by the link, never by the Worker body. */
export interface LinkFailure {
  readonly kind: "failure";
  readonly why: string;
}

/** The port to the Worker, abstract so a test can drive the body in Node. */
export interface WorkerLink {
  post(msg: WorkerRequest): void;
  onReply(listener: (reply: WorkerReply | LinkFailure) => void): void;
  close(): void;
}

export interface WorkerHostOptions {
  readonly setup: WorkerSetup;
  /** Wall-clock milliseconds the Worker may plan for; 0 leaves it to the abort. */
  readonly budgetMs?: number;
  /** Hash every plan instead of once per player-turn. D2's `--verify`. */
  readonly verify?: boolean;
  /** Where a desync is reported. Defaults to `console.warn`. */
  readonly warn?: (message: string) => void;
}

/**
 * After an abort the Worker is asked to stop and given this long to answer
 * with its best command so far. A planner that never yields cannot see the
 * abort until it finishes, so a long plan outlives the grace and the turn
 * falls back to the main thread; the late reply is dropped by request id.
 */
const ABORT_GRACE_MS = 1500;

/** A Worker that desyncs this often is not a replay client; stop re-initing
 *  it and let the main thread play the rest of the game. */
const MAX_REINITS = 8;

interface Inflight {
  readonly id: number;
  readonly done: (cmd: GameCommand | null) => void;
}

function turnKey(s: GameState): string {
  const t = s.turn;
  return `${String(t.month)}:${String(t.week)}:${String(t.day)}:${String(t.activePlayer)}`;
}

export class WorkerAiHost {
  private readonly link: WorkerLink;
  private readonly feed: CommandFeed;
  private readonly opts: WorkerHostOptions;
  private readonly unsubscribe: () => void;

  /** The version the Worker will be at once everything posted is replayed. */
  private version = 0;
  /** Accepted since the last flush, with the version the last one produced. */
  private pending: GameCommand[] = [];
  private head = 0;

  private nextId = 1;
  private inflight: Inflight | null = null;
  private checkedTurn = "";
  private desyncCount = 0;
  private reinits = 0;
  private dead = false;
  private disposed = false;
  /** Between posting an init and its `ready`, every other reply is stale: it
   *  answers a request posted before the init and already resolved here. */
  private awaitingReady = false;

  constructor(link: WorkerLink, feed: CommandFeed, opts: WorkerHostOptions) {
    this.link = link;
    this.feed = feed;
    this.opts = opts;
    this.unsubscribe = feed.onCommand((cmd, version) => {
      this.pending.push(cmd);
      this.head = version;
    });
    link.onReply((r) => { this.onReply(r); });
    this.init(true);
  }

  /** How many times the desync check fired. The M3.5 gate reads this. */
  get desyncs(): number { return this.desyncCount; }

  /** False once the Worker has been given up on; every plan then falls back. */
  get alive(): boolean { return !this.dead && !this.disposed; }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    this.inflight?.done(null);
    this.inflight = null;
    this.link.close();
  }

  /**
   * Ask the Worker for `actor`'s next command from the session's current
   * state. Null means "no answer": the caller plans on the main thread.
   */
  plan(actor: PlayerId, signal: AbortSignal, budgetMs = this.opts.budgetMs ?? 0): Promise<GameCommand | null> {
    if (!this.alive || this.inflight !== null) return Promise.resolve(null);
    const state = this.feed.getState();

    if (this.pending.length > 0) {
      this.link.post({ kind: "commands", from: this.version, commands: this.pending });
      this.pending = [];
      this.version = this.head;
    }
    // A command the feed did not report cannot be replayed. It cannot happen
    // through `GameSession`, whose only mutation point reports every accept,
    // but a snapshot is the honest answer if it ever does.
    if (this.version !== state.version) this.init(false);

    const key = turnKey(state);
    const check = this.opts.verify === true || key !== this.checkedTurn;
    if (check) this.checkedTurn = key;

    const id = this.nextId++;
    return new Promise<GameCommand | null>((resolve) => {
      let grace: ReturnType<typeof setTimeout> | null = null;
      const done = (cmd: GameCommand | null): void => {
        if (this.inflight !== null && this.inflight.id === id) this.inflight = null;
        if (grace !== null) clearTimeout(grace);
        signal.removeEventListener("abort", onAbort);
        resolve(cmd);
      };
      const onAbort = (): void => {
        this.link.post({ kind: "abort", id });
        grace = setTimeout(() => { done(null); }, ABORT_GRACE_MS);
      };
      this.inflight = { id, done };
      if (signal.aborted) onAbort();
      else signal.addEventListener("abort", onAbort, { once: true });
      this.link.post({ kind: "plan", id, actor, version: state.version, budgetMs, check });
    });
  }

  private onReply(r: WorkerReply | LinkFailure): void {
    if (this.disposed) return;
    if (this.awaitingReady && r.kind !== "ready" && r.kind !== "failure") return;
    switch (r.kind) {
      case "ready":
        this.awaitingReady = false;
        return;
      case "command": {
        const req = this.inflight;
        // A reply to a request the grace period already answered.
        if (req === null || req.id !== r.id) return;
        if (r.hash !== null) {
          const mine = this.feed.getState();
          // The loop is parked on this plan, so nothing has moved since it
          // was asked; a version that did move is a request that was already
          // abandoned, and its hash says nothing about either state.
          if (mine.version === r.version) {
            const ours = hashState(mine);
            if (ours !== r.hash) {
              this.desync(r.version, `hash mismatch at version ${String(r.version)}: main ${String(ours)}, worker ${String(r.hash)}`);
              req.done(null);
              return;
            }
          }
        }
        req.done(r.cmd);
        return;
      }
      case "desync":
        this.desync(r.at, r.why);
        this.inflight?.done(null);
        return;
      case "failure":
        this.warn(`AI worker failed; the main thread plays from here: ${r.why}`);
        this.dead = true;
        this.inflight?.done(null);
        return;
    }
  }

  private desync(at: number, why: string): void {
    this.desyncCount += 1;
    this.warn(`AI worker desync (D7 check fired, #${String(this.desyncCount)}) at version ${String(at)}: ${why}`);
    this.init(false);
  }

  /** The one full clone: at game start, and after a desync. */
  private init(withContent: boolean): void {
    if (!withContent) {
      this.reinits += 1;
      if (this.reinits > MAX_REINITS) {
        this.warn(`AI worker re-initialised ${String(MAX_REINITS)} times; giving up on it for this game.`);
        this.dead = true;
        return;
      }
    }
    const state = this.feed.getState();
    this.pending = [];
    this.version = state.version;
    this.head = state.version;
    this.checkedTurn = "";
    const msg: WorkerRequest = withContent
      ? { kind: "init", state, setup: this.opts.setup, content: this.feed.getContent() }
      : { kind: "init", state, setup: this.opts.setup };
    this.awaitingReady = true;
    this.link.post(msg);
  }

  private warn(message: string): void {
    (this.opts.warn ?? ((m: string): void => { console.warn(m); }))(message);
  }
}

/**
 * The computer seat as the loop sees it: `chooseCommand(obs, signal)` like any
 * other controller. The observation itself is not sent - the Worker builds its
 * own from its own state - so a plan costs one small message each way.
 */
export class WorkerAiController implements PlayerController {
  readonly kind = "ai" as const;
  private readonly host: WorkerAiHost;
  private readonly fallback: AiController;

  constructor(host: WorkerAiHost, fallback: AiController) {
    this.host = host;
    this.fallback = fallback;
  }

  async chooseCommand(obs: PlayerObservation, signal: AbortSignal): Promise<GameCommand> {
    const cmd = obs.viewer === "spectator" ? null : await this.host.plan(obs.viewer, signal);
    if (cmd !== null) return cmd;
    return this.fallback.chooseCommand(obs, signal);
  }
}

// ------------------------------------------------------------ the real link

/** Wrap a browser Worker as a link. */
export function linkTo(worker: Worker): WorkerLink {
  return {
    post(msg) { worker.postMessage(msg); },
    onReply(listener) {
      worker.onmessage = (e: MessageEvent): void => { listener(e.data as WorkerReply); };
      worker.onerror = (e: ErrorEvent): void => {
        listener({ kind: "failure", why: e.message || "uncaught error in the worker" });
      };
    },
    close() { worker.terminate(); },
  };
}

/** Spawn the Vite module worker. Only valid where `Worker` exists. */
export function spawnAiWorker(): WorkerLink {
  return linkTo(new Worker(new URL("./aiWorker.ts", import.meta.url), { type: "module" }));
}

/**
 * Whether this page should plan in a Worker at all. Node and the headless
 * runner have no `Worker`; a developer can force the main thread with
 * `?ai=main` on the URL or `localStorage["realm.aiWorker"] = "main"`, which is
 * the cheapest way to tell a Worker bug from a rule bug.
 */
export function workerAiEnabled(): boolean {
  if (typeof Worker === "undefined") return false;
  try {
    const g = globalThis as { location?: { search?: string }; localStorage?: { getItem(k: string): string | null } };
    if (g.location?.search !== undefined && new URLSearchParams(g.location.search).get("ai") === "main") return false;
    if (g.localStorage?.getItem("realm.aiWorker") === "main") return false;
  } catch {
    // A sandboxed page throws on storage access; the Worker is still fine.
  }
  return true;
}
